import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import AnimatedSection from './AnimatedSection';
import { HandDrawnDivider, VintageFrame } from './DecorativeElements';

const Packages = () => {
  const { language } = useLanguage();

  const packageCategories = [
    {
      name: language === 'en' ? 'Wedding Packages' : 'Paquetes de Boda',
      icon: '💍',
      description: language === 'en' 
        ? 'Complete wedding packages tailored to your needs'
        : 'Paquetes de boda completos adaptados a tus necesidades',
    },
    {
      name: language === 'en' ? 'Corporate Events' : 'Eventos Corporativos',
      icon: '🏢',
      description: language === 'en'
        ? 'Professional packages for business gatherings'
        : 'Paquetes profesionales para reuniones de negocios',
    },
    {
      name: language === 'en' ? 'Celebration Packages' : 'Paquetes de Celebración',
      icon: '🎉',
      description: language === 'en'
        ? 'Special packages for birthdays, anniversaries, and more'
        : 'Paquetes especiales para cumpleaños, aniversarios y más',
    },
  ];

  return (
    <section id="packages" className="section-padding bg-warm-beige texture-overlay">
      <div className="container mx-auto">
        <AnimatedSection animation="fadeInUp">
          <div className="text-center mb-16">
            <h2 className="section-heading">
              {language === 'en' ? 'Event Packages' : 'Paquetes de Eventos'}
            </h2>
            <p className="text-lg md:text-xl font-serif text-terracotta italic mb-4">
              {language === 'en'
                ? 'Tailored packages for every occasion'
                : 'Paquetes adaptados para cada ocasión'}
            </p>
            <HandDrawnDivider />
          </div>
        </AnimatedSection>

        {/* Coming Soon Banner */}
        <AnimatedSection animation="fadeInUp" delay={200}>
          <div className="max-w-3xl mx-auto mb-16">
            <VintageFrame>
              <div className="bg-rustic-cream p-8 md:p-12 rounded-lg text-center">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-3xl md:text-4xl font-serif text-deep-brown mb-4">
                  {language === 'en' ? 'Packages Coming Soon!' : '¡Paquetes Próximamente!'}
                </h3>
                <p className="text-lg md:text-xl font-body text-deep-brown/80 mb-6 leading-relaxed">
                  {language === 'en'
                    ? 'We are currently crafting exclusive event packages tailored to make your special day unforgettable. Check back soon for our complete package offerings, or contact us to discuss custom options for your event.'
                    : 'Actualmente estamos creando paquetes de eventos exclusivos adaptados para hacer tu día especial inolvidable. Vuelve pronto para ver nuestras ofertas de paquetes completos, o contáctanos para discutir opciones personalizadas para tu evento.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
                  <a
                    href="#contact"
                    className="btn-primary"
                  >
                    {language === 'en' ? 'Contact Us for Custom Packages' : 'Contáctanos para Paquetes Personalizados'}
                  </a>
                  <a
                    href="#services"
                    className="btn-secondary"
                  >
                    {language === 'en' ? 'View Our Services' : 'Ver Nuestros Servicios'}
                  </a>
                </div>
              </div>
            </VintageFrame>
          </div>
        </AnimatedSection>

        {/* Package Categories Preview */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packageCategories.map((category, index) => (
            <AnimatedSection 
              key={category.name}
              animation="scaleIn" 
              delay={300 + (index * 100)}
            >
              <div className="card-rustic text-center group">
                <div className="p-8">
                  <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                    {category.icon}
                  </div>
                  <h3 className="text-2xl font-serif text-deep-brown mb-3 group-hover:text-terracotta transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-deep-brown/70 font-body">
                    {category.description}
                  </p>
                  <div className="mt-4 pt-4 border-t border-latte">
                    <span className="text-sm text-terracotta italic font-body">
                      {language === 'en' ? 'Coming Soon' : 'Próximamente'}
                    </span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Additional Info */}
        <AnimatedSection animation="fadeInUp" delay={600}>
          <div className="text-center mt-16">
            <p className="text-lg font-body text-deep-brown mb-6">
              {language === 'en'
                ? 'While we finalize our package offerings, we\'re happy to create a custom package tailored specifically to your event needs.'
                : 'Mientras finalizamos nuestras ofertas de paquetes, estamos felices de crear un paquete personalizado adaptado específicamente a las necesidades de tu evento.'}
            </p>
            <a
              href="#contact"
              className="btn-secondary inline-block"
            >
              {language === 'en' ? 'Get a Custom Quote' : 'Obtener una Cotización Personalizada'}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Packages;
